/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ApiParameter, get } from "@insite/client-framework/Services/ApiService";
import { BaseModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { FeaturedDocumentItemInfoType } from "@stoneage/App/Handlers/Documents/getFeaturedDocumentsData";

const featuredDocumentsUri = "/api/sainc/featuredDocuments";
const productDocumentsUri = "/api/sainc/resourceCenter/productDocumentCollection";

type FeaturedDocumentsInformationResult = {
    uri: string;
    featuredDocuments: FeaturedDocumentItemInfoType[];
    properties: any;
    successful: boolean;
    error?: any;
};

export async function getFeaturedDocumentInformation(props: any) {
    try {
        const result = await get<FeaturedDocumentsInformationResult>(
            `${featuredDocumentsUri}?documentType=${props.documentType}&numberOfDocuments=${props.numberOfDocuments}`,
        );
        return {
            successful: true,
            uri: result.uri,
            featuredDocuments: result.featuredDocuments,
            properties: result.properties,
        };
    } catch (error) {
        return {
            successful: false,
            featuredDocuments: [],
        };
    }
}

export type ProductDocument = {
    title: string;
    documentType: string;
    isObselete: boolean;
    lastModifiedOn: string;
    url: string;
    description: string;
};

export type ProductDocumentsFacets = {
    id: string;
    name: string;
    count: number;
    sortOrder: number;
    selected: boolean;
};

export interface ProductInformationDocumentsModel extends BaseModel {
    pagination: PaginationModel;
    productDocuments: ProductDocument[];
    applicationFacets: ProductDocumentsFacets[];
    documentTypeFacets: ProductDocumentsFacets[];
    productLineFacets: ProductDocumentsFacets[];
}

export interface ProductInformationDocumentsApiParameters extends ApiParameter {
    doFacetedSearches?: boolean;
    onlyShowMyProducts?: boolean;
    onlyShowActiveDocuments?: boolean;
    onlyShowDiscontinuedDocuments?: boolean;
    pageSize?: number;
    page?: number;
    applications?: string;
    documentTypes?: string;
    productLines?: string;
    sortBy?: string;
    timestamp?: string;
    query?: string;
}

export async function getProductInformationDocuments(props: ProductInformationDocumentsApiParameters) {
    const timestamp = `time${new Date().getTime()}`;
    props.timestamp = timestamp;
    try {
        const result = await get<ProductInformationDocumentsModel>(`${productDocumentsUri}`, props);
        return {
            successful: true,
            data: result,
        };
    } catch (error) {
        return {
            successful: false,
            data: undefined,
        };
    }
}
